import CheckIconAnimated from '@/components/ui/CheckIconAnimated';
import RefetchIcon from '@/components/ui/RefetchIcon';
import { useEffect, useRef, useState } from 'react';
import Snippet from './snippet';

type Props = {
  isPreviewMode: boolean;
  setPreviewMode: (b: boolean) => void;
  isRefetching: boolean;
  databaseId?: number;
  refetch: () => void;
  uri: string;
  children?: React.ReactNode;
  page?: string;
};
function Toolbar({
  isPreviewMode,
  setPreviewMode,
  databaseId,
  isRefetching,
  refetch,
  uri,
  children,
  page = 'page',
}: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const state = useRef({ fetchDone: false, minDone: false });
  const prevIsRefetching = useRef(isRefetching);

  const tryFinish = () => {
    if (state.current.fetchDone && state.current.minDone) {
      setIsLoading(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    }
  };

  useEffect(() => {
    if (prevIsRefetching.current && !isRefetching && isLoading) {
      state.current.fetchDone = true;
      tryFinish();
    }
    prevIsRefetching.current = isRefetching;
  }, [isRefetching]);

  const onRefetch = () => {
    state.current = { fetchDone: false, minDone: false };
    setIsLoading(true);
    setShowSuccess(false);
    refetch();
    setTimeout(() => {
      state.current.minDone = true;
      tryFinish();
    }, 1000);
  };

  const editUrl = databaseId
    ? `${process.env.WORDPRESS_URL}/wp-admin/post.php?post=${databaseId}&action=edit`
    : '#';

  return (
    <div className="flex">
      <div className="fixed top-4 left-4 z-50 grid grid-cols-[auto_250px] gap-1">
        <div className="flex flex-col gap-1">
          <div className="flex bg-gray-100 rounded p-1">
            <button
              onClick={() => setPreviewMode(false)}
              className={`flex-1 px-3 py-1 text-xs rounded transition-all ${
                !isPreviewMode
                  ? 'bg-white text-green-700 shadow font-bold'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Live
            </button>
            <button
              onClick={() => setPreviewMode(true)}
              className={`flex-1 px-3 py-1 text-xs rounded transition-all ${
                isPreviewMode
                  ? 'bg-white text-blue-600 shadow font-bold'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Preview
            </button>
          </div>
          <div className="flex gap-1">
            <a
              href={editUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs rounded text-white flex-1 bg-dark-blue py-1 no-underline grid place-content-center"
            >
              Edit
            </a>
            <button
              className={`aspect-square grid place-content-center rounded border [&>svg]:w-4 h-6 transition-colors ${
                showSuccess
                  ? 'bg-green-500 text-white border-green-500'
                  : 'bg-white text-black border-black'
              } ${isLoading ? '[&>svg]:animate-spin' : ''}`}
              onClick={onRefetch}
              disabled={isLoading}
            >
              {showSuccess ? <CheckIconAnimated /> : <RefetchIcon />}
            </button>
          </div>
        </div>
        <div className="opacity-25 hover:opacity-100 transition-opacity">
          {children}
        </div>
      </div>
      <Snippet page={page} props={`uri="${uri}"`} />
    </div>
  );
}

export default Toolbar;
